// Picker карточки документа (Form1): организация зависит от субъекта РФ.

import { useState, useCallback, useEffect } from 'react';
import {
  branchPickerOptionsForSubject,
  fetchBranchPickerOptionsForSubject,
  resolveSubjectRefId,
  resolveSubjectName,
} from './pickerHooks';

import { addressChildren, addressOptions, resolveForestAddress } from '../../../utils/forestAddress';

const DEPENDENT_LABEL_FIELDS = ['filial', 'lvo', 'ulvo', 'urc'];

async function loadCardFilialOptions(handbooks, localFormData, dependentData) {
  const subjectId = resolveSubjectRefId(localFormData.srf, handbooks);
  if (!subjectId) return [];

  const fromState = dependentData?.organizations || [];
  if (fromState.length > 0) return fromState;

  // Уже был subject-data при выборе субъекта (даже если branches: [])
  if (String(dependentData?.subjectDataLoadedFor || '') === String(subjectId)) {
    return fromState;
  }

  const fromHandbook = branchPickerOptionsForSubject(handbooks, subjectId);
  if (fromHandbook.length > 0) return fromHandbook;

  const subjectName = resolveSubjectName(localFormData.srf, handbooks);
  return fetchBranchPickerOptionsForSubject(subjectId, subjectName);
}

// Набор «разрешённых» значений для нового документа на основе профиля пользователя.
// Если ограничение не применяется — возвращаем null (показываем всё).
function buildAllowedSets(user, isNewCard) {
  if (!isNewCard || !user) return { subjectIds: null, subjectNames: null, branchNames: null };

  const regions = Array.isArray(user.regions) ? user.regions : [];
  const subjectIds = regions.length ? new Set(regions.map((r) => String(r.id))) : null;
  const subjectNames = regions.length
    ? new Set(regions.map((r) => String(r.name || '').trim().toLowerCase()))
    : null;

  // Филиал пользователя (у администратора LEVEL3 — «Администратор», ограничение не нужно).
  const branchName = String(user.branch_name || '').trim();
  const branchNames =
    branchName && user.role !== 'LEVEL3' && branchName.toLowerCase() !== 'администратор'
      ? new Set([branchName.toLowerCase()])
      : null;

  return { subjectIds, subjectNames, branchNames };
}

export const useCardPicker = (
  handbooks,
  localFormData,
  dependentData,
  setFormData,
  setLocalFormData,
  user = null,
  isNewCard = false
) => {
  const allowed = buildAllowedSets(user, isNewCard);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerField, setPickerField] = useState('');
  const [pickerOptions, setPickerOptions] = useState([]);
  const [pickerValue, setPickerValue] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const [filialLoading, setFilialLoading] = useState(false);

  const openPickerOptions = useCallback((fieldName, options) => {
    const currentValue = localFormData[fieldName] || '';
    const addressField = ['lvo', 'ulvo', 'urc'].includes(fieldName);
    const addressId = addressField ? resolveForestAddress(localFormData, handbooks)[fieldName]?.id : null;
    const comparableValue = fieldName === 'srf'
      ? (resolveSubjectRefId(currentValue, handbooks) || '')
      : addressField ? String(addressId ?? '') : currentValue;
    const selectedIndex = options.findIndex(opt => opt.value === comparableValue);
    setPickerField(fieldName);
    setPickerOptions(options);
    setPickerValue(comparableValue);
    setPickerSearch('');
    setSelectedOptionIndex(selectedIndex);
    setShowPicker(true);
  }, [handbooks, localFormData]);

  const openPicker = async (fieldName, handbookId) => {
    if (fieldName === 'filial') {
      if (!resolveSubjectRefId(localFormData.srf, handbooks)) {
        alert('Сначала выберите «Субъект РФ»');
        return;
      }
      setFilialLoading(true);
      try {
        let options = await loadCardFilialOptions(handbooks, localFormData, dependentData);
        // На создании карточки показываем только доступные пользователю филиалы.
        if (allowed.branchNames) {
          options = options.filter((o) => allowed.branchNames.has(String(o.label || '').trim().toLowerCase()));
        }
        if (options.length > 0) {
          if (!(dependentData?.organizations?.length > 0) && setLocalFormData) {
            setLocalFormData((prev) => ({
              ...prev,
              dependentData: {
                ...(prev.dependentData || {}),
                organizations: options,
              },
            }));
          }
          openPickerOptions(fieldName, options);
          return;
        }
        alert(
          'Для выбранного субъекта РФ в справочнике нет организаций (филиалов). Выберите другой субъект или обратитесь к администратору справочников.'
        );
      } finally {
        setFilialLoading(false);
      }
      return;
    }

    let options = [];
    const address = resolveForestAddress(localFormData, handbooks);
    if (fieldName === 'lvo') {
      options = addressOptions(addressChildren(handbooks, 36, address.srf?.id));
    } else if (fieldName === 'ulvo') {
      options = addressOptions(addressChildren(handbooks, 37, address.lvo?.id));
    } else if (fieldName === 'urc') {
      options = addressOptions(addressChildren(handbooks, 38, address.ulvo?.id));
    } else {
      const handbook = handbookByIdLoose(handbooks, handbookId);
      if (handbook?.references) {
        options = handbook.references.map((r) => ({
          value: r.id.toString(),
          label: r.name,
          description: r.description || null,
        }));
      }
      // На создании карточки «Субъект РФ» (организация пользователя) ограничиваем
      // субъектами из профиля пользователя (по id или по названию).
      if (fieldName === 'srf' && (allowed.subjectIds || allowed.subjectNames)) {
        options = options.filter(
          (o) =>
            (allowed.subjectIds && allowed.subjectIds.has(String(o.value))) ||
            (allowed.subjectNames && allowed.subjectNames.has(String(o.label || '').trim().toLowerCase()))
        );
      }
    }

    if (options?.length > 0) {
      openPickerOptions(fieldName, options);
      return;
    }
    if (fieldName === 'lvo') {
      alert('Сначала выберите «Субъект РФ» и «Организацию, проводившую работу»');
    }
  };

  const closePicker = () => {
    setShowPicker(false);
    setPickerField('');
    setPickerOptions([]);
    setPickerValue('');
    setPickerSearch('');
    setSelectedOptionIndex(-1);
  };

  const selectPickerValue = (value) => {
    const selectedOption = pickerOptions.find((opt) => opt.value === value);
    const isDependentLabelField = DEPENDENT_LABEL_FIELDS.includes(pickerField);
    const valueToSave = isDependentLabelField && selectedOption ? selectedOption.label : value;

    const newDependentData = { ...dependentData };
    if (pickerField === 'filial' && pickerOptions.length) {
      newDependentData.organizations = pickerOptions;
    }

    const newData = {
      ...localFormData,
      [pickerField]: valueToSave,
      dependentData: newDependentData,
    };

    if (['lvo', 'ulvo', 'urc'].includes(pickerField)) newData[`${pickerField}Id`] = value;
    const descendants = { srf: ['lvo', 'ulvo', 'urc'], lvo: ['ulvo', 'urc'], ulvo: ['urc'] };
    const previousId = localFormData[`${pickerField}Id`];
    const changed = previousId ? String(previousId) !== String(value) : localFormData[pickerField] !== valueToSave;
    for (const field of changed ? descendants[pickerField] || [] : []) {
      newData[field] = '';
      newData[`${field}Id`] = '';
    }
    newData.isUrcRequired = resolveForestAddress(newData, handbooks).isUrcRequired;

    if (setFormData) {
      setFormData(newData);
    }
    closePicker();
  };

  const getFilteredOptions = useCallback(() => {
    let filtered = pickerOptions;
    if (pickerSearch) {
      filtered = pickerOptions.filter(
        (option) =>
          option.label.toLowerCase().includes(pickerSearch.toLowerCase()) ||
          (option.description &&
            option.description.toLowerCase().includes(pickerSearch.toLowerCase()))
      );
    }
    return filtered.sort((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase(), 'ru')
    );
  }, [pickerOptions, pickerSearch]);

  const getSelectedIndexInFiltered = useCallback(() => {
    if (selectedOptionIndex < 0) return -1;
    const filteredOptions = getFilteredOptions();
    return filteredOptions.findIndex((opt) => opt.value === pickerValue);
  }, [selectedOptionIndex, pickerValue, getFilteredOptions]);

  useEffect(() => {
    if (!showPicker) return;
    const timer = setTimeout(() => {
      const filteredIndex = getSelectedIndexInFiltered();
      if (filteredIndex >= 0) {
        const selectedElement = document.querySelector(
          `.picker-list li:nth-child(${filteredIndex + 1})`
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [showPicker, pickerSearch, selectedOptionIndex, getSelectedIndexInFiltered]);

  return {
    showPicker,
    pickerField,
    pickerOptions,
    pickerValue,
    pickerSearch,
    selectedOptionIndex,
    filialLoading,
    openPicker,
    closePicker,
    selectPickerValue,
    getFilteredOptions,
    getSelectedIndexInFiltered,
    setPickerSearch,
  };
};

function handbookByIdLoose(handbooks, handbookId) {
  if (!handbooks?.length || handbookId == null) return undefined;
  const want = String(handbookId);
  return handbooks.find((h) => String(h.id) === want);
}
